// Omnix/client/ui/AudioFileManager.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class AudioFileManager extends ComponentBase {
    static styles = `
        .audio-file-manager {
            height: 100%;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            position: relative;
        }
        .afm-header {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            flex-shrink: 0;
        }
        .afm-body {
            flex-grow: 1;
            overflow-y: auto;
            padding: 1rem;
        }
        .afm-group-header button {
            font-weight: 500;
            text-align: left;
            width: 100%;
        }
        .afm-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.75rem;
        }
        .afm-item-icon {
            font-size: 1.5rem;
            color: var(--bs-secondary-color);
        }
        .afm-item-details {
            flex-grow: 1;
            overflow: hidden;
        }
        .afm-item-name {
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .afm-item-meta {
            font-size: 0.8rem;
            color: var(--bs-secondary-color);
        }
        .afm-item-player {
            flex-shrink: 0;
            width: 40px;
            text-align: center;
        }
        .afm-item-player .btn {
            font-size: 1.5rem;
        }
        .afm-item.selectable {
            cursor: pointer;
        }
        .afm-item.selectable:hover, .afm-item.is-playing {
            background-color: var(--bs-primary-bg-subtle);
        }
        .afm-shared-player {
            display: none;
        }
    `;

    constructor(config) {
        super(config);
        this.audioFiles = [];
        this.groupedFiles = {};
        this.isSelectionMode = this.config.isSelectionMode || false;
        this.sharedAudioPlayer = null;
        this.currentPlayingFileId = null;
        this.isPlaying = false;
        this.audioRecorder = null;
        this.isInitialized = false;

        // Bind all methods to prevent context issues
        this.handleClick = this.handleClick.bind(this);
        this.handleRecordingSaved = this.handleRecordingSaved.bind(this);
        this._onPlay = this._onPlay.bind(this);
        this._onPause = this._onPause.bind(this);
        this._onEnded = this._onEnded.bind(this);

        this.applyStyles();
    }

    async initialize() {
        if (this.isInitialized) return;

        this.container = document.createElement('div');
        this.container.className = 'audio-file-manager';

        this.sharedAudioPlayer = document.createElement('audio');
        this.sharedAudioPlayer.className = 'afm-shared-player';
        this.container.appendChild(this.sharedAudioPlayer);

        this.sharedAudioPlayer.addEventListener('play', this._onPlay);
        this.sharedAudioPlayer.addEventListener('pause', this._onPause);
        this.sharedAudioPlayer.addEventListener('ended', this._onEnded);

        // Create audio recorder
        this.audioRecorder = await this.createManagedComponent('AudioRecorder', document.body, {
            instanceId: `${this.config.instanceId}-recorder`
        });
        this.audioRecorder.on('recordingSaved', this.handleRecordingSaved);

        // Attach single click handler
        this.container.addEventListener('click', this.handleClick);

        this.isInitialized = true;
        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadAudioFiles();
    }

    handleClick(e) {
        const target = e.target.closest('[data-action]');
        if (!target) {
            if (this.isSelectionMode) {
                const itemElement = e.target.closest('.afm-item.selectable');
                if (itemElement) this.emit('fileSelected', { fileId: itemElement.dataset.fileId });
            }
            return;
        }

        const action = target.dataset.action;
        const itemElement = target.closest('.afm-item');
        const fileId = itemElement?.dataset.fileId;

        switch (action) {
            case 'play-pause': if (fileId) this.handlePlayPause(fileId); break;
            case 'upload': this.showUploadModal(); break;
            case 'record': this.audioRecorder.show(); break;
            case 'tts': this.showTTSModal(); break;
            case 'edit': if (fileId) this.showEditModal(fileId); break;
            case 'delete': if (fileId) this.handleDelete(fileId); break;
        }
    }

    _onPlay() {
        this.isPlaying = true;
        this._updateAllPlayPauseIcons();
    }

    _onPause() {
        this.isPlaying = false;
        this._updateAllPlayPauseIcons();
    }

    _onEnded() {
        this.isPlaying = false;
        this.currentPlayingFileId = null;
        this._updateAllPlayPauseIcons();
    }

    _updateAllPlayPauseIcons() {
        this.container.querySelectorAll('.afm-item').forEach(item => {
            const fileId = item.dataset.fileId;
            const playPauseBtn = item.querySelector('[data-action="play-pause"] i');
            const itemContainer = item.closest('.afm-item');

            if (!playPauseBtn || !itemContainer) return;

            itemContainer.classList.remove('is-playing');
            playPauseBtn.className = 'bi bi-play-circle-fill';

            if (fileId === this.currentPlayingFileId) {
                if (this.isPlaying) {
                    playPauseBtn.className = 'bi bi-pause-circle-fill text-primary';
                    itemContainer.classList.add('is-playing');
                } else {
                    playPauseBtn.className = 'bi bi-play-circle-fill text-primary';
                    itemContainer.classList.add('is-playing');
                }
            }
        });
    }

    handleRecordingSaved({ file }) {
        this.log.info("Received recorded audio file:", file);
        this.showUploadModal({ prefilledFile: file });
    }

    async onShow() {
        await this.loadAudioFiles();
    }

    async loadAudioFiles() {
        try {
            const result = await this.appClient.rpcClient.call('AudioFileManagerRpc.getData', {
                pageSize: 1000,
                sort: { group: 1, name: 1 }
            });
            this.audioFiles = result.data || [];
            this.groupFiles();
            if (this.container) this.render();
        } catch (error) {
            this.log.error("Failed to load audio files:", error);
        }
    }

    groupFiles() {
        this.groupedFiles = this.audioFiles.reduce((acc, file) => {
            (acc[file.group] = acc[file.group] || []).push(file);
            return acc;
        }, {});
    }

    render() {
        // Remove existing content properly
        const existingHeader = this.container.querySelector('.afm-header');
        const existingBody = this.container.querySelector('.afm-body');
        if (existingHeader) existingHeader.remove();
        if (existingBody) existingBody.remove();

        const headerDiv = document.createElement('div');
        headerDiv.className = 'afm-header d-flex justify-content-between align-items-center';
        headerDiv.innerHTML = `
            <h5 class="mb-0" data-i18n="Audio File Manager">Audio File Manager</h5>
            <div class="btn-group">
                <button class="btn btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="bi bi-plus-lg"></i> ${this.t('Add Audio')}
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                    <li><a class="dropdown-item" href="#" data-action="upload"><i class="bi bi-upload me-2"></i>${this.t('Upload File')}</a></li>
                    <li><a class="dropdown-item" href="#" data-action="record"><i class="bi bi-mic-fill me-2"></i>${this.t('Record Audio')}</a></li>
                    <li><a class="dropdown-item" href="#" data-action="tts"><i class="bi bi-soundwave me-2"></i>${this.t('Create from Text')}</a></li>
                </ul>
            </div>
        `;

        const bodyDiv = document.createElement('div');
        bodyDiv.className = 'afm-body';
        bodyDiv.innerHTML = '<div class="accordion" id="audio-groups-accordion"></div>';

        // Insert in correct order: sharedAudioPlayer (already exists), header, then body
        this.container.appendChild(headerDiv);
        this.container.appendChild(bodyDiv);

        const accordionContainer = this.container.querySelector('#audio-groups-accordion');
        if (Object.keys(this.groupedFiles).length === 0) {
            accordionContainer.innerHTML = `<p class="text-muted text-center p-3">${this.t('No audio files found. Add one to get started.')}</p>`;
        } else {
            Object.entries(this.groupedFiles).forEach(([groupName, files], index) => {
                accordionContainer.appendChild(this.renderGroup(groupName, files, index));
            });
        }

        return this.container;
    }

    renderGroup(groupName, files, index) {
        const accordionItem = document.createElement('div');
        accordionItem.className = 'accordion-item';
        const isFirst = index === 0;

        accordionItem.innerHTML = `
            <h2 class="accordion-header" id="heading-${index}">
                <button class="accordion-button ${isFirst ? '' : 'collapsed'}" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-${index}">
                    ${this.t(groupName)} <span class="badge bg-secondary ms-2">${files.length}</span>
                </button>
            </h2>
            <div id="collapse-${index}" class="accordion-collapse collapse ${isFirst ? 'show' : ''}" data-bs-parent="#audio-groups-accordion">
                <div class="accordion-body p-0">
                    <div class="list-group list-group-flush"></div>
                </div>
            </div>
        `;

        const listGroup = accordionItem.querySelector('.list-group');
        files.forEach(file => {
            listGroup.appendChild(this.renderItem(file));
        });

        return accordionItem;
    }

    renderItem(file) {
        const item = document.createElement('div');
        item.className = `list-group-item afm-item ${this.isSelectionMode ? 'selectable' : ''}`;
        item.dataset.fileId = file._id;

        const sourceIcons = {
            upload: 'bi-file-earmark-arrow-up-fill',
            recording: 'bi-mic-fill',
            tts: 'bi-soundwave'
        };

        const playButtonHtml = `
            <button class="btn btn-link p-0" data-action="play-pause" title="${this.t('Play/Pause')}">
                <i class="bi bi-play-circle-fill"></i>
            </button>
        `;

        item.innerHTML = `
            <i class="bi ${sourceIcons[file.sourceType] || 'bi-file-earmark-music'} afm-item-icon" title="${this.t(file.sourceType)}"></i>
            <div class="afm-item-details">
                <div class="afm-item-name">${file.name}</div>
                <div class="afm-item-meta">${(file.size / 1024).toFixed(1)} KB &middot; ${file.mimeType}</div>
            </div>
            <div class="afm-item-player">
                ${playButtonHtml}
            </div>
            <div class="afm-item-actions btn-group btn-group-sm">
                <button class="btn btn-outline-secondary" data-action="edit" title="Edit"><i class="bi bi-pencil"></i></button>
                <button class="btn btn-outline-danger" data-action="delete" title="Delete"><i class="bi bi-trash"></i></button>
            </div>
        `;
        return item;
    }

    async handlePlayPause(fileId) {
        const fileData = this.audioFiles.find(f => f._id === fileId);
        if (!fileData) return;

        if (this.currentPlayingFileId === fileId) {
            if (this.isPlaying) {
                this.sharedAudioPlayer.pause();
            } else {
                this.sharedAudioPlayer.play();
            }
        } else {
            this.currentPlayingFileId = fileId;
            this.isPlaying = false;
            this._updateAllPlayPauseIcons();

            try {
                const result = await this.appClient.rpcClient.call('AudioFileManagerRpc.getPresignedUrl', {
                    bucketName: fileData.bucketName,
                    objectName: fileData.objectName
                });
                this.sharedAudioPlayer.src = result.url;
                this.sharedAudioPlayer.play();
            } catch (error) {
                this.appClient.notifier.error("Could not load audio for playback.");
                this.currentPlayingFileId = null;
                this._updateAllPlayPauseIcons();
            }
        }
    }

    async showUploadModal(options = {}) {
        const { prefilledFile } = options;

        const formConfig = {
            title: prefilledFile ? "Save Recorded Audio" : "Upload New Audio File",
            submitButtonLabel: "Upload and Save",
            fields: [
                { name: 'name', type: 'text', label: 'File Name', required: true },
                { name: 'group', type: 'text', label: 'Group Name', required: true, placeholder: 'e.g., IVR Greetings' },
                { name: 'description', type: 'textarea', label: 'Description' },
                {
                    name: 'audioFile',
                    label: 'Audio File',
                    component: 'FileUpload',
                    autoUpload: false,
                    getUploadKey: async () => {
                        const formData = uploadForm.getFormData();
                        const groupName = formData.group;
                        if (!groupName) {
                            this.appClient.notifier.warn("Please enter a Group Name first.");
                            return null;
                        }
                        return await this.appClient.rpcClient.call('AudioFileManagerRpc.getUploadKey', { group: groupName });
                    },
                    allowedFileTypes: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3'],
                    maxFiles: 1
                }
            ]
        };

        const uploadForm = await this.createManagedComponent('GenericFormRenderer', document.body, {
            instanceId: `${this.config.instanceId}-upload-form-${Date.now()}`,
            formId: `${this.config.instanceId}-upload-form-id-${Date.now()}`,
            layout: 'dialog',
            formConfig: formConfig,
        });

        const fileUploadComponent = uploadForm.fieldComponents.get('audioFile');

        if (prefilledFile) {
            uploadForm.setData({ name: prefilledFile.name.replace(/\.[^/.]+$/, "") });
            if (fileUploadComponent) {
                await fileUploadComponent.addFiles([prefilledFile]);
            }
        }

        const handleSubmit = async () => {
            const formData = uploadForm.getFormData();
            if (!fileUploadComponent || fileUploadComponent.files.length === 0) {
                this.appClient.notifier.error("Please provide an audio file.");
                uploadForm.setSubmitting(false);
                return;
            }
            try {
                const uploadKey = await fileUploadComponent.startUpload();
                if (!uploadKey) {
                    uploadForm.setSubmitting(false);
                    return;
                }
                await this.appClient.rpcClient.call('AudioFileManagerRpc.createFromUpload', {
                    name: formData.name,
                    group: formData.group,
                    description: formData.description,
                    uploadKey: uploadKey
                });
                this.appClient.notifier.success("Audio file uploaded and saved.");
                await this.loadAudioFiles();
                uploadForm.destroy();
            } catch (error) {
                this.appClient.notifier.error(`Upload failed: ${error.message}`);
                uploadForm.setSubmitting(false);
            }
        };

        const handleDestroy = () => {
            uploadForm.off('formSubmit', handleSubmit);
            uploadForm.off('destroyed', handleDestroy);
        };

        uploadForm.on('formSubmit', handleSubmit);
        uploadForm.on('destroyed', handleDestroy);
    }

    async showTTSModal() {
        const formConfig = {
            title: "Create Audio from Text",
            submitButtonLabel: "Generate and Save",
            fields: [
                { name: 'name', type: 'text', label: 'File Name', required: true },
                { name: 'group', type: 'text', label: 'Group Name', required: true },
                { name: 'description', type: 'textarea', label: 'Description' },
                { name: 'text', type: 'textarea', label: 'Text to Synthesize', required: true, rows: 5 },
                { name: 'voiceId', component: 'VoiceSelector', label: 'Voice', required: true }
            ]
        };

        const form = await this.createManagedComponent('GenericFormRenderer', document.body, {
            instanceId: `${this.config.instanceId}-tts-form`,
            layout: 'dialog',
            formConfig: formConfig,
        });

        const modalBody = form.formElement.querySelector('.modal-body');
        const previewContainer = document.createElement('div');
        previewContainer.className = 'mt-3 p-3 border rounded bg-light';
        previewContainer.innerHTML = `
            <button type="button" class="btn btn-secondary w-100" id="tts-preview-btn">${this.t('Preview Audio')}</button>
            <audio class="w-100 mt-2" id="tts-preview-player" controls></audio>
        `;
        modalBody.appendChild(previewContainer);

        const previewBtn = previewContainer.querySelector('#tts-preview-btn');
        const audioPlayer = previewContainer.querySelector('#tts-preview-player');

        previewBtn.addEventListener('click', async () => {
            const formData = form.getFormData();
            if (!formData.text || !formData.voiceId) {
                this.appClient.notifier.warn("Please enter text and select a voice to generate a preview.");
                return;
            }
            previewBtn.disabled = true;
            previewBtn.innerHTML = `<span class="spinner-border spinner-border-sm"></span> ${this.t('Generating...')}`;
            try {
                const result = await this.appClient.rpcClient.call('AudioFileManagerRpc.previewTTSWithVoice', {
                    text: formData.text, 
                    voiceId: formData.voiceId 
                });
                audioPlayer.src = result.audioDataUrl;
                audioPlayer.play();
            } catch (error) {
                this.appClient.notifier.error(`Preview failed: ${error.message}`);
            } finally {
                previewBtn.disabled = false;
                previewBtn.innerHTML = this.t('Preview Audio');
            }
        });

        const handleSubmit = async () => {
            const formData = form.getFormData();
            try {
                await this.appClient.rpcClient.call('AudioFileManagerRpc.createFromTTSWithVoice', formData);
                this.appClient.notifier.success("TTS audio created and saved.");
                await this.loadAudioFiles();
                form.destroy();
            } catch (error) {
                this.appClient.notifier.error(`TTS creation failed: ${error.message}`);
                form.setSubmitting(false);
            }
        };

        form.on('formSubmit', handleSubmit);
        form.on('destroyed', () => form.off('formSubmit', handleSubmit));
    }


    async showEditModal(fileId) {
        const fileData = this.audioFiles.find(f => f._id === fileId);
        if (!fileData) {
            this.appClient.notifier.error("Could not find the audio file to edit.");
            return;
        }

        const formConfig = {
            title: "Edit Audio File Details",
            submitButtonLabel: "Save Changes",
            fields: [
                { name: 'name', type: 'text', label: 'File Name', required: true },
                { name: 'group', type: 'text', label: 'Group Name', required: true },
                { name: 'description', type: 'textarea', label: 'Description' },
            ]
        };

        const editForm = await this.createManagedComponent('GenericFormRenderer', document.body, {
            instanceId: `${this.config.instanceId}-edit-form-${Date.now()}`,
            formId: `${this.config.instanceId}-edit-form-id-${Date.now()}`,
            layout: 'dialog',
            formConfig: formConfig,
        });

        editForm.setData(fileData);

        const handleEditSubmit = async () => {
            const formData = editForm.getFormData();
            try {
                await this.appClient.rpcClient.call('AudioFileManagerRpc.update', { _id: fileId, ...formData });
                this.appClient.notifier.success("Audio file details updated.");
                await this.loadAudioFiles();
                editForm.destroy();
            } catch (error) {
                this.appClient.notifier.error(`Update failed: ${error.message}`);
                editForm.setSubmitting(false);
            }
        };

        const handleEditDestroy = () => {
            editForm.off('formSubmit', handleEditSubmit);
            editForm.off('destroyed', handleEditDestroy);
        };

        editForm.on('formSubmit', handleEditSubmit);
        editForm.on('destroyed', handleEditDestroy);
    }

    async handleDelete(fileId) {
        const confirmed = await this.appClient.notifier.confirm("Are you sure you want to delete this audio file permanently?");
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('AudioFileManagerRpc.delete', { _id: fileId });
                this.appClient.notifier.success("Audio file deleted.");
                await this.loadAudioFiles();
            } catch (error) {
                this.appClient.notifier.error(`Delete failed: ${error.message}`);
            }
        }
    }

    destroy() {
        if (this.sharedAudioPlayer) {
            this.sharedAudioPlayer.pause();
            this.sharedAudioPlayer.src = '';
            this.sharedAudioPlayer.removeEventListener('play', this._onPlay);
            this.sharedAudioPlayer.removeEventListener('pause', this._onPause);
            this.sharedAudioPlayer.removeEventListener('ended', this._onEnded);
        }

        if (this.container && this.handleClick) {
            this.container.removeEventListener('click', this.handleClick);
        }

        if (this.audioRecorder) {
            this.audioRecorder.off('recordingSaved', this.handleRecordingSaved);
        }

        super.destroy();
    }
}